import {
  Component,
  effect,
  ElementRef,
  inject,
  OnDestroy,
  signal,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';

import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToolbarModule } from 'primeng/toolbar';
import { TooltipModule } from 'primeng/tooltip';
import * as docxPreview from 'docx-preview';
import { DocumentService } from '../../services/document.service';
import { DocxViewerService } from '../../services/docx-viewer.service';

@Component({
  selector: 'app-docx-viewer',
  standalone: true,
  imports: [DialogModule, ButtonModule, ProgressSpinnerModule, ToolbarModule, TooltipModule],
  templateUrl: './docx-viewer.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./docx-viewer.component.scss'],
})
export class DocxViewerComponent implements OnDestroy {
  @ViewChild('docxContainer', { static: false }) docxContainer?: ElementRef<HTMLDivElement>;

  private documentService = inject(DocumentService);
  private docxViewerService = inject(DocxViewerService);

  loading = signal(false);
  error = signal<string | null>(null);

  localVisible = signal(false);

  private currentBlobUrl: string | null = null;

  data = this.docxViewerService.document;
  constructor() {
    effect(() => {
      const serviceVisible = this.docxViewerService.visible();
      if (serviceVisible && !this.localVisible()) {
        this.localVisible.set(true);
      }
    });
  }

  ngOnDestroy() {
    this.cleanup();
  }

  onShow() {
    this.loadDocument();
  }

  onVisibilityChange(visible: boolean) {
    this.localVisible.set(visible);
    if (!visible) {
      this.onClose();
    }
  }

  onClose() {
    this.localVisible.set(false);
    this.cleanup();
    this.docxViewerService.closeDocument();
  }

  async loadDocument() {
    const currentData = this.data();
    if (!currentData?.objectStoreRef) {
      this.error.set('No document reference provided');
      return;
    }

    this.loading.set(true);
    this.error.set(null);

    this.documentService.getDocumentBlob(currentData.objectStoreRef).subscribe({
      next: async (blob) => {
        const validMimeTypes = [
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          'application/msword',
        ];

        // More lenient check - rely mainly on file extension
        const isDocx =
          currentData.filename.toLowerCase().endsWith('.docx') || currentData.filename.toLowerCase().endsWith('.doc');
        const hasValidMimeType = validMimeTypes.includes(blob.type);

        if (!isDocx && !hasValidMimeType) {
          this.error.set('Document is not a Word file');
          this.loading.set(false);
          return;
        }

        if (this.currentBlobUrl) {
          this.documentService.revokeBlobUrl(this.currentBlobUrl);
        }

        try {
          // Wait for next tick to ensure the container is available
          await new Promise((resolve) => setTimeout(resolve, 100));

          if (this.docxContainer?.nativeElement) {
            // Clear any existing content
            this.docxContainer.nativeElement.innerHTML = '';

            console.log('Rendering DOCX document...');
            await docxPreview.renderAsync(blob, this.docxContainer.nativeElement, undefined, {
              className: 'docx-preview',
              inWrapper: true,
              ignoreWidth: false,
              ignoreHeight: false,
              ignoreFonts: false,
              breakPages: true,
              ignoreLastRenderedPageBreak: true,
              experimental: true,
              trimXmlDeclaration: true,
              useBase64URL: false,
              renderHeaders: true,
              renderFooters: true,
              renderFootnotes: true,
              renderEndnotes: true,
            });
            console.log('DOCX document rendered successfully');
            this.loading.set(false);
          } else {
            console.error('DOCX container element not found');
            this.error.set('Unable to display document - container not found');
            this.loading.set(false);
          }
        } catch (err) {
          console.error('Failed to render DOCX document:', err);
          this.error.set('Failed to render Word document. You can still download it.');
          this.loading.set(false);
        }
      },
      error: (err) => {
        console.error('Failed to load document:', err);
        let errorMessage = 'Failed to load document';

        if (err.status === 401) {
          errorMessage = 'You are not authorized to view this document';
        } else if (err.status === 404) {
          errorMessage = 'Document not found';
        } else if (err.status >= 500) {
          errorMessage = 'Server error occurred while loading document';
        }

        this.error.set(errorMessage);
        this.loading.set(false);
      },
    });
  }

  downloadDocument() {
    const currentData = this.data();
    if (currentData?.objectStoreRef && currentData?.filename) {
      this.documentService.downloadDocument(currentData.objectStoreRef, currentData.filename);
    }
  }

  private cleanup() {
    if (this.currentBlobUrl) {
      this.documentService.revokeBlobUrl(this.currentBlobUrl);
      this.currentBlobUrl = null;
    }
    if (this.docxContainer?.nativeElement) {
      this.docxContainer.nativeElement.innerHTML = '';
    }
    this.error.set(null);
  }
}
